import { ApplicationErrorCode } from '@oysterjs/core/graphql';
import type { ActionResult } from '@oysterjs/core/graphql/actions';
import { getApplicationError, getValidationErrors } from '@oysterjs/core/graphql/errors';
import { redirect } from 'react-router-dom';
import type { DataResult } from '~/api/data/result';

/** Resolve query errors and navigation consistently for data handlers. */
export const loadQuery = async <T>(
  result: Promise<DataResult<T>>,
  options?: { notFoundRedirect?: string; suppressAuthErrors?: boolean }
) => {
  const { data, error } = await result;
  const applicationError = getApplicationError(error);
  if (applicationError?.code === ApplicationErrorCode.Unauthorized) {
    return options?.suppressAuthErrors ? data : redirect('/signin');
  }
  if (applicationError?.code === ApplicationErrorCode.NotFound && options?.notFoundRedirect) {
    return redirect(options.notFoundRedirect);
  }
  if (error) throw error;
  return data;
};

/** Keep form validation results and success redirects compatible with route action consumers. */
export const submitMutation = async <T>(
  result: Promise<DataResult<T>>,
  onSuccess?: (data: T) => Response | ActionResult<T> | Promise<Response | ActionResult<T>>
) => {
  const { data, error } = await result;
  const applicationError = getApplicationError(error);
  const validationErrors = getValidationErrors(error);
  if (applicationError?.code === ApplicationErrorCode.Unauthorized) return redirect('/signin');
  if (applicationError) return { applicationError };
  if (validationErrors?.length) return { validationErrors };
  if (error) throw error;
  return onSuccess ? onSuccess(data) : { data };
};
